// frontend/src/components/TenantPortal/ConnectDataPanel.jsx
// Phase 2 — "Connect Your Data" slide-over panel
// Shown to Tenant Admin (r4) when they click "Data Settings" in the App Gallery.
// Zero impact on non-admin users or existing platform dashboards.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  X, Database, CheckCircle2, AlertCircle, Loader2,
  ChevronDown, ChevronRight, RefreshCw, Unlink, Info,
  ArrowRightLeft, Plug, Workflow, ArrowDownToLine, ArrowUpFromLine, Save,
  AlertTriangle, Copy, Check, FileCode2, Folder,
} from 'lucide-react';
import { apiFetch } from '../../api';
import { useAuth } from '../../context/useAuth';
import { useTimezoneFormatter } from '../../hooks/useTimezoneFormatter';

// ─── Status badge config ────────────────────────────────────────────────────
const STATUS_CONFIG = {
  READY: {
    icon: <CheckCircle2 className="w-4 h-4" />,
    label: 'Your Data',
    cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  },
  REFRESHING: {
    icon: <Loader2 className="w-4 h-4 animate-spin" />,
    label: 'Syncing…',
    cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  },
  FAILED: {
    icon: <AlertCircle className="w-4 h-4" />,
    label: 'Sync Failed',
    cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  },
  PENDING: {
    icon: <Database className="w-4 h-4" />,
    label: 'Platform Data',
    cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  },
};

// ─── Individual model card ───────────────────────────────────────────────────
function ModelCard({ model, connections, onRefreshStatus }) {
  const { token } = useAuth();
  const { formatDate } = useTimezoneFormatter();
  const [expanded, setExpanded] = useState(false);
  const [selectedConn, setSelectedConn] = useState(model.override?.connection_id || '');
  const [mapping, setMapping] = useState(model.override?.schema_mapping || {});
  const [showMapping, setShowMapping] = useState(false);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(model.override?.data_status === 'REFRESHING');
  const [status, setStatus] = useState(model.override?.data_status || 'PENDING');
  const [lastRefreshed, setLastRefreshed] = useState(model.override?.last_refreshed_at);
  const [lastError, setLastError] = useState(model.override?.last_error);
  const [showError, setShowError] = useState(false);
  const [confirmRevert, setConfirmRevert] = useState(false);
  const pollRef = useRef(null);

  // ── Schema validation state ─────────────────────────────────────────────
  const [validating, setValidating] = useState(false);
  const [validation, setValidation] = useState(null); // { tables, summary }
  const [showValidation, setShowValidation] = useState(false);
  const [copiedDDL, setCopiedDDL] = useState(null); // table name currently copied

  // ── Poll while syncing ──────────────────────────────────────────────────
  const poll = useCallback(async () => {
    try {
      const res = await apiFetch(
        `/tenant-portal/data-connect/overrides/${model.model_id}`,
        token,
      );
      const s = res.data_status || res.status || 'PENDING';
      setStatus(s);
      setLastRefreshed(res.last_refreshed_at);
      setLastError(res.last_error);
      if (s !== 'REFRESHING') {
        clearInterval(pollRef.current);
        setSyncing(false);
        onRefreshStatus?.();
      }
    } catch (_) { /* non-fatal */ }
  }, [model.model_id, token, onRefreshStatus]);

  useEffect(() => {
    if (syncing) {
      pollRef.current = setInterval(poll, 3000);
    }
    return () => clearInterval(pollRef.current);
  }, [syncing, poll]);

  // ── Validate schema when connection changes ─────────────────────────────
  const validateSchema = useCallback(async (connId) => {
    if (!connId) { setValidation(null); return; }
    setValidating(true);
    try {
      const res = await apiFetch(
        '/tenant-portal/data-connect/validate-schema',
        token,
        {
          method: 'POST',
          body: JSON.stringify({
            connection_id: connId,
            platform_model_id: model.model_id,
            schema_mapping: mapping,
          }),
        },
      );
      setValidation(res);
      setShowValidation(true);
    } catch (err) {
      console.error('Schema validation failed:', err);
      setValidation(null);
    } finally {
      setValidating(false);
    }
  }, [model.model_id, token, mapping]);

  // Auto-validate when connection selection changes or card is expanded
  useEffect(() => {
    if (selectedConn && expanded) {
      validateSchema(selectedConn);
    }
  }, [selectedConn, expanded]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCopyDDL = (tableName, ddl) => {
    navigator.clipboard.writeText(ddl);
    setCopiedDDL(tableName);
    setTimeout(() => setCopiedDDL(null), 2000);
  };

  // ── Save config ─────────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    try {
      await apiFetch(
        `/tenant-portal/data-connect/overrides/${model.model_id}`,
        token,
        {
          method: 'PUT',
          body: JSON.stringify({
            connection_id: selectedConn,
            schema_mapping: Object.keys(mapping).length > 0 ? mapping : null,
          }),
        },
      );
    } catch (err) {
      console.error('Save failed:', err);
    } finally {
      setSaving(false);
    }
  };

  // ── Trigger sync ─────────────────────────────────────────────────────────
  const handleSync = async () => {
    if (!selectedConn) return;
    await handleSave(); // persist config first
    setSyncing(true);
    setStatus('REFRESHING');
    setLastError(null);
    try {
      await apiFetch(
        `/tenant-portal/data-connect/overrides/${model.model_id}/sync`,
        token,
        { method: 'POST' },
      );
    } catch (err) {
      setSyncing(false);
      setStatus('FAILED');
      setLastError(String(err));
    }
  };

  // ── Revert to platform data ────────────────────────────────────────
  const handleRevert = async () => {
    try {
      await apiFetch(
        `/tenant-portal/data-connect/overrides/${model.model_id}`,
        token,
        { method: 'DELETE' },
      );
      setStatus('PENDING');
      setLastRefreshed(null);
      setConfirmRevert(false);
      onRefreshStatus?.();
    } catch (err) {
      console.error('Revert failed:', err);
      setConfirmRevert(false);
    }
  };

  const badge = STATUS_CONFIG[status] || STATUS_CONFIG.PENDING;

  return (
    <div className="border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
      {/* Card header */}
      <button
        className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-750 transition-colors text-left"
        onClick={() => setExpanded(e => !e)}
      >
        <div className="flex items-center gap-3 min-w-0">
          <Database className="w-4 h-4 text-slate-400 shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">
              {model.model_name}
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {model.tables.length} table{model.tables.length !== 1 ? 's' : ''}
              {lastError?.startsWith('PARTIAL_SYNC') && status === 'READY' && (
                <span className="ml-1 text-amber-500">⚠ partial</span>
              )}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0 ml-2">
          {/* Status badge */}
          <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${badge.cls}`}>
            {badge.icon}
            {badge.label}
          </span>
          {expanded
            ? <ChevronDown className="w-4 h-4 text-slate-400" />
            : <ChevronRight className="w-4 h-4 text-slate-400" />
          }
        </div>
      </button>

      {/* Expanded config */}
      {expanded && (
        <div className="px-4 py-4 space-y-4 bg-white dark:bg-slate-900">

          {/* Error banner */}
          {status === 'FAILED' && lastError && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <div>
                <strong>Last sync failed.</strong>
                {!showError && (
                  <button
                    className="ml-2 underline text-red-600 dark:text-red-400"
                    onClick={() => setShowError(true)}
                  >
                    View Error →
                  </button>
                )}
                {showError && (
                  <pre className="mt-2 text-xs whitespace-pre-wrap font-mono bg-red-100 dark:bg-red-900/30 p-2 rounded">
                    {lastError}
                  </pre>
                )}
                <p className="mt-1 text-xs text-red-500">Dashboards are currently showing platform sample data.</p>
              </div>
            </div>
          )}

          {/* Last refreshed */}
          {status === 'READY' && lastRefreshed && (
            <div className="flex items-center gap-2 text-xs text-emerald-600 dark:text-emerald-400">
              <CheckCircle2 className="w-3.5 h-3.5" />
              Last synced: {formatDate(lastRefreshed)}
            </div>
          )}

          {/* Tables list — expandable to show column definitions */}
          <div>
            <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">
              Tables in this model
              <span className="ml-1 normal-case font-normal opacity-70">— click to view columns</span>
            </p>
            <div className="space-y-1">
              {model.tables.map(t => (
                <details key={t.name} className="group rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
                  <summary className="flex items-center gap-2 px-3 py-2 cursor-pointer list-none bg-slate-50 dark:bg-slate-800/60 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
                    <ChevronRight className="w-3 h-3 text-slate-400 shrink-0 group-open:rotate-90 transition-transform" />
                    <Database className="w-3 h-3 text-slate-400 shrink-0" />
                    <span className="text-xs font-mono text-slate-700 dark:text-slate-300 flex-1">{t.name}</span>
                    <span className="text-[10px] text-slate-400">{t.columns?.length || 0} cols</span>
                  </summary>
                  {t.columns && t.columns.length > 0 && (
                    <div className="px-3 py-2 bg-white dark:bg-slate-900 border-t border-slate-100 dark:border-slate-800">
                      <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                        {t.columns.map((col, idx) => (
                          <div key={col.name || idx} className="flex items-center gap-1.5 min-w-0">
                            <span className="text-[10px] font-mono text-slate-600 dark:text-slate-400 truncate flex-1">
                              {col.name || col}
                            </span>
                            {col.data_type && (
                              <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium shrink-0 ${
                                col.data_type === 'number' ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400' :
                                col.data_type === 'date'   ? 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400' :
                                col.data_type === 'boolean'? 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400' :
                                col.data_type === 'json'   ? 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400' :
                                'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400'
                              }`}>
                                {col.data_type}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </details>
              ))}
            </div>
          </div>

          {/* Step 1: Connection */}
          <div>
            <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">
              Step 1 — Select Datasource Connection
            </label>
            <select
              value={selectedConn}
              onChange={e => setSelectedConn(e.target.value)}
              className="w-full text-sm border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-500"
            >
              <option value="">— Select a connection —</option>
              {connections.map(c => (
                <option key={c.id} value={c.id}>{c.name || c.label || c.id}</option>
              ))}
            </select>
            {connections.length === 0 && (
              <p className="mt-1 text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
                <Info className="w-3 h-3" />
                No connections found. Add one in <strong>Settings → Connections</strong>.
              </p>
            )}
          </div>

          {/* Schema validation results */}
          {validating && (
            <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400 py-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Checking table compatibility…
            </div>
          )}

          {validation && showValidation && (
            <div className="space-y-2">
              {/* Summary banner */}
              <div className={`flex items-center gap-2 p-3 rounded-lg text-sm ${
                validation.summary.missing > 0
                  ? 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-300'
                  : 'bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300'
              }`}>
                {validation.summary.missing > 0
                  ? <AlertTriangle className="w-4 h-4 shrink-0" />
                  : <CheckCircle2 className="w-4 h-4 shrink-0" />
                }
                <div className="flex-1">
                  <strong>{validation.summary.found}</strong> of {validation.summary.total_tables} tables found
                  {validation.summary.missing > 0 && (
                    <span> — <strong>{validation.summary.missing} missing</strong></span>
                  )}
                  <p className="text-xs mt-0.5 opacity-80">
                    Database: {validation.database} ({validation.db_type})
                  </p>
                </div>
                <button
                  onClick={() => setShowValidation(false)}
                  className="text-xs underline opacity-70 hover:opacity-100"
                >
                  Hide
                </button>
              </div>

              {/* Per-table grid */}
              <div className="max-h-64 overflow-y-auto space-y-1 rounded-lg border border-slate-200 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-800">
                {validation.tables.map(t => (
                  <div key={t.platform_table} className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      {t.exists
                        ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                        : <AlertCircle className="w-3.5 h-3.5 text-red-400 shrink-0" />
                      }
                      <span className={`text-xs font-mono flex-1 truncate ${
                        t.exists ? 'text-slate-700 dark:text-slate-300' : 'text-red-500 dark:text-red-400'
                      }`}>
                        {t.platform_table}
                      </span>
                      {t.exists ? (
                        <span className="text-[10px] text-slate-400">
                          {t.row_count >= 0 ? `${t.row_count.toLocaleString()} rows` : ''}
                          {t.column_match && (
                            <span className="ml-1">
                              {t.column_match.missing?.length > 0
                                ? <span className="text-amber-500">{t.column_match.matched}/{t.column_match.total_expected} cols</span>
                                : <span className="text-emerald-500">{t.column_match.matched}/{t.column_match.total_expected} cols</span>
                              }
                            </span>
                          )}
                        </span>
                      ) : (
                        <span className="text-[10px] font-medium text-red-400">Missing</span>
                      )}
                    </div>

                    {/* Missing columns warning */}
                    {t.exists && t.column_match?.missing?.length > 0 && (
                      <div className="mt-1 ml-5 text-[10px] text-amber-500">
                        Missing columns: {t.column_match.missing.join(', ')}
                      </div>
                    )}

                    {/* CREATE TABLE DDL for missing tables */}
                    {!t.exists && t.create_ddl && (
                      <details className="mt-1.5 ml-5 group">
                        <summary className="text-[10px] text-slate-400 cursor-pointer list-none flex items-center gap-1 hover:text-slate-600 dark:hover:text-slate-300">
                          <FileCode2 className="w-3 h-3" />
                          <span>Show CREATE TABLE script</span>
                          <ChevronRight className="w-2.5 h-2.5 group-open:rotate-90 transition-transform" />
                        </summary>
                        <div className="mt-1 relative">
                          <pre className="text-[10px] font-mono bg-slate-100 dark:bg-slate-800 p-2 rounded-md overflow-x-auto text-slate-700 dark:text-slate-300 whitespace-pre-wrap">
                            {t.create_ddl}
                          </pre>
                          <button
                            onClick={() => handleCopyDDL(t.platform_table, t.create_ddl)}
                            className="absolute top-1.5 right-1.5 p-1 rounded bg-white dark:bg-slate-700 shadow-sm border border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors"
                            title="Copy to clipboard"
                          >
                            {copiedDDL === t.platform_table
                              ? <Check className="w-3 h-3 text-emerald-500" />
                              : <Copy className="w-3 h-3 text-slate-400" />
                            }
                          </button>
                        </div>
                      </details>
                    )}
                  </div>
                ))}
              </div>

              {/* Warning about partial sync */}
              {validation.summary.missing > 0 && (
                <p className="text-[11px] text-amber-600 dark:text-amber-400 flex items-center gap-1">
                  <Info className="w-3 h-3 shrink-0" />
                  Sync will skip missing tables. Dashboards using them will show empty data.
                </p>
              )}
            </div>
          )}

          {/* Partial sync warning from last sync */}
          {status === 'READY' && lastError?.startsWith('PARTIAL_SYNC') && !showError && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-sm text-amber-700 dark:text-amber-300">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <div>
                <strong>Partial sync.</strong> Some tables were not found in your database.
                <button
                  className="ml-2 underline text-amber-600 dark:text-amber-400"
                  onClick={() => setShowError(true)}
                >
                  View Details →
                </button>
                {showError && (
                  <p className="mt-1 text-xs text-amber-500">{lastError.replace('PARTIAL_SYNC: ', '')}</p>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Optional schema mapping */}
          <div>
            <button
              className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
              onClick={() => setShowMapping(m => !m)}
            >
              <ArrowRightLeft className="w-3.5 h-3.5" />
              Step 2 — Column / Table Mapping
              <span className="ml-1 text-slate-400">(optional)</span>
              {showMapping ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            </button>

            {showMapping && (
              <div className="mt-3 space-y-3">
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  If your table or column names differ from the platform schema, enter the mapping below.
                  Leave blank to use platform names as-is.
                </p>
                {model.tables.map(t => (
                  <TableMappingRow
                    key={t.name}
                    platformTable={t.name}
                    columns={t.columns.map(c => typeof c === 'string' ? c : c.name)}
                    mapping={mapping[t.name] || {}}
                    onChange={tblMapping =>
                      setMapping(prev => ({ ...prev, [t.name]: tblMapping }))
                    }
                  />
                ))}
              </div>
            )}
          </div>

          {/* Step 3: Actions */}
          <div className="pt-2 border-t border-slate-100 dark:border-slate-800 space-y-2">
            {/* Inline revert confirmation */}
            {confirmRevert ? (
              <div className="flex items-center gap-2 p-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                <span className="text-xs text-amber-700 dark:text-amber-300 flex-1">
                  Revert to platform sample data?
                </span>
                <button
                  onClick={handleRevert}
                  className="text-xs px-2.5 py-1 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors font-medium"
                >
                  Confirm
                </button>
                <button
                  onClick={() => setConfirmRevert(false)}
                  className="text-xs px-2.5 py-1 bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 rounded-md transition-colors"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <button
                  onClick={() => setConfirmRevert(true)}
                  disabled={status === 'PENDING'}
                  className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 hover:text-red-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <Unlink className="w-3.5 h-3.5" />
                  Revert to Platform Data
                </button>

                <button
                  onClick={handleSync}
                  disabled={!selectedConn || syncing}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg
                             bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700
                             disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
                >
                  {syncing
                    ? <><Loader2 className="w-4 h-4 animate-spin" /> Syncing…</>
                    : <><RefreshCw className="w-4 h-4" /> Sync Now</>
                  }
                </button>
              </div>
            )}
          </div>

        </div>
      )}
    </div>
  );
}

// ─── Per-table mapping row (optional step 2) ─────────────────────────────────
function TableMappingRow({ platformTable, columns, mapping, onChange }) {
  const [tableAlt, setTableAlt] = useState(mapping.tenant_table || '');
  const [colMap, setColMap] = useState(mapping.columns || {});

  const update = (newTable, newCols) => {
    const result = {};
    if (newTable) result.tenant_table = newTable;
    if (Object.keys(newCols).length > 0) result.columns = newCols;
    onChange(Object.keys(result).length > 0 ? result : {});
  };

  return (
    <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-3 space-y-2">
      {/* Table rename */}
      <div className="flex items-center gap-2">
        <span className="text-xs text-slate-500 w-24 shrink-0 font-mono">{platformTable}</span>
        <ArrowRightLeft className="w-3 h-3 text-slate-300" />
        <input
          type="text"
          placeholder={`Your table (e.g. ${platformTable}_master)`}
          value={tableAlt}
          onChange={e => {
            setTableAlt(e.target.value);
            update(e.target.value, colMap);
          }}
          className="flex-1 text-xs border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded px-2 py-1 font-mono"
        />
      </div>
      {/* Column renames */}
      {columns.length > 0 && (
        <details className="group">
          <summary className="text-xs text-slate-400 cursor-pointer list-none flex items-center gap-1">
            <ChevronRight className="w-3 h-3 group-open:rotate-90 transition-transform" />
            Map columns ({columns.length})
          </summary>
          <div className="mt-2 space-y-1 pl-4">
            {columns.map(col => (
              <div key={col} className="flex items-center gap-2">
                <span className="text-xs text-slate-500 w-32 shrink-0 font-mono truncate">{col}</span>
                <ArrowRightLeft className="w-3 h-3 text-slate-300 shrink-0" />
                <input
                  type="text"
                  placeholder={col}
                  value={colMap[col] || ''}
                  onChange={e => {
                    const nc = { ...colMap, [col]: e.target.value };
                    if (!e.target.value) delete nc[col];
                    setColMap(nc);
                    update(tableAlt, nc);
                  }}
                  className="flex-1 text-xs border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded px-2 py-1 font-mono"
                />
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

// ─── Flow node card ─────────────────────────────────────────────────────────
function FlowNodeCard({ flow, connections, instanceId, savedOverrides, onSaved, autoExpand }) {
  const { token } = useAuth();

  // DB-source / sink state
  const [nodeConns, setNodeConns] = useState(() => {
    const init = {};
    flow.nodes.forEach(n => { init[n.node_id] = n.override_connection_id || ''; });
    return init;
  });
  const [nodeTable, setNodeTable] = useState(() => {
    const init = {};
    flow.nodes.forEach(n => { init[n.node_id] = n.override_table || ''; });
    return init;
  });
  const [nodeCustomSql, setNodeCustomSql] = useState(() => {
    const init = {};
    flow.nodes.forEach(n => { init[n.node_id] = n.override_custom_sql || ''; });
    return init;
  });
  const [nodeDestTable, setNodeDestTable] = useState(() => {
    const init = {};
    flow.nodes.forEach(n => { init[n.node_id] = n.override_dest_table || ''; });
    return init;
  });
  const [showSql, setShowSql] = useState({});

  // File-source state (FILE_LISTENER, REMOTE_FILE, SFTP, etc.)
  const [nodeStorageType, setNodeStorageType] = useState(() => {
    const init = {};
    flow.nodes.forEach(n => { init[n.node_id] = n.override_storage_type || 'local'; });
    return init;
  });
  const [nodeFolderPath, setNodeFolderPath] = useState(() => {
    const init = {};
    flow.nodes.forEach(n => { init[n.node_id] = n.override_folder_path || ''; });
    return init;
  });
  const [nodeFilePatterns, setNodeFilePatterns] = useState(() => {
    const init = {};
    flow.nodes.forEach(n => { init[n.node_id] = n.override_file_patterns || '*.*'; });
    return init;
  });

  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [expanded, setExpanded] = useState(!!autoExpand);

  // Readiness check per role
  const isNodeReady = (n) => {
    if (n.role === 'source')      return !!nodeConns[n.node_id] && (!!nodeTable[n.node_id] || !!nodeCustomSql[n.node_id]);
    if (n.role === 'sink')        return !!nodeConns[n.node_id];
    if (n.role === 'file_source') return !!nodeFolderPath[n.node_id];
    return true;
  };
  const mappedCount = flow.nodes.filter(n => isNodeReady(n)).length;
  const totalCount  = flow.nodes.length;
  const allMapped   = totalCount > 0 && mappedCount === totalCount;

  const handleSave = async () => {
    if (!instanceId) return;
    setSaving(true);
    try {
      const patch = {};
      flow.nodes.forEach(n => {
        const entry = {};
        if (n.role === 'file_source') {
          // Save file-path config
          if (nodeStorageType[n.node_id])  entry.storageType   = nodeStorageType[n.node_id];
          if (nodeFolderPath[n.node_id])   entry.folderPath    = nodeFolderPath[n.node_id];
          if (nodeFilePatterns[n.node_id]) entry.filePatterns  = nodeFilePatterns[n.node_id];
        } else {
          // Save DB-connection config
          if (nodeConns[n.node_id]) {
            const conn = connections.find(c => c.id === nodeConns[n.node_id]);
            entry.connectionId = nodeConns[n.node_id];
            entry.label = conn?.name || conn?.label || nodeConns[n.node_id];
          }
          if (nodeTable[n.node_id])     entry.table            = nodeTable[n.node_id];
          if (nodeCustomSql[n.node_id]) entry.customSql        = nodeCustomSql[n.node_id];
          if (nodeDestTable[n.node_id]) entry.destinationTable = nodeDestTable[n.node_id];
        }
        if (Object.keys(entry).length > 0) patch[n.node_id] = entry;
      });
      const existing = savedOverrides || {};
      const merged = { ...existing, [flow.flow_id]: patch };
      await apiFetch(`/tenant-portal/data-connect/instances/${instanceId}/flow-config`, token, {
        method: 'PUT',
        body: JSON.stringify({ flow_config: merged }),
      });
      setSaved(true);
      setTimeout(() => { setSaved(false); setExpanded(false); }, 1200);
      onSaved?.();
    } catch (err) {
      console.error('Flow config save failed:', err);
    } finally {
      setSaving(false);
    }
  };

  // Role badge styling
  const roleBadge = (role) => {
    if (role === 'source')      return 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400';
    if (role === 'sink')        return 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400';
    if (role === 'file_source') return 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400';
    return '';
  };
  const roleLabel = (role) => {
    if (role === 'source')      return 'db source';
    if (role === 'sink')        return 'output';
    if (role === 'file_source') return 'file source';
    return role;
  };

  return (
    <div className={`border rounded-xl overflow-hidden transition-colors duration-200 ${
      allMapped ? 'border-emerald-200 dark:border-emerald-800' : 'border-slate-200 dark:border-slate-700'
    }`}>
      {/* Accordion header */}
      <button
        type="button"
        onClick={() => setExpanded(prev => !prev)}
        className={`w-full flex items-center gap-2 px-4 py-3 text-left transition-colors ${
          allMapped
            ? 'bg-emerald-50 dark:bg-emerald-900/20 hover:bg-emerald-100 dark:hover:bg-emerald-900/30'
            : 'bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700/60'
        }`}
      >
        <ChevronRight className={`w-3.5 h-3.5 text-slate-400 shrink-0 transition-transform duration-200 ${expanded ? 'rotate-90' : ''}`} />
        <Workflow className={`w-4 h-4 shrink-0 ${allMapped ? 'text-emerald-500' : 'text-violet-500'}`} />
        <p className="text-sm font-semibold text-slate-900 dark:text-slate-100 flex-1 truncate">{flow.flow_name}</p>
        {allMapped ? (
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
            <CheckCircle2 className="w-3 h-3" /> All configured
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
            {mappedCount}/{totalCount} configured
          </span>
        )}
      </button>

      <div className={`transition-all duration-200 ease-in-out overflow-hidden ${expanded ? 'max-h-[4000px] opacity-100' : 'max-h-0 opacity-0'}`}>
        <div className="px-4 py-3 space-y-4 bg-white dark:bg-slate-900">
          {flow.nodes.map(n => (
            <div key={n.node_id} className="border border-slate-100 dark:border-slate-800 rounded-xl p-3 space-y-3">

              {/* Node header: icon + label + role badge + ready check */}
              <div className="flex items-center gap-2">
                {n.role === 'file_source'
                  ? <Folder className="w-3.5 h-3.5 text-orange-500" />
                  : n.role === 'source'
                    ? <ArrowDownToLine className="w-3.5 h-3.5 text-blue-500" />
                    : <ArrowUpFromLine className="w-3.5 h-3.5 text-emerald-500" />
                }
                <span className="text-xs font-semibold text-slate-700 dark:text-slate-300 flex-1">{n.node_label}</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${roleBadge(n.role)}`}>
                  {roleLabel(n.role)}
                </span>
                {isNodeReady(n) && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />}
              </div>

              {/* ── FILE SOURCE config ── */}
              {n.role === 'file_source' && (
                <div className="space-y-2">
                  {/* Storage Type */}
                  <div>
                    <label className="text-[10px] font-medium text-slate-400 uppercase tracking-wide mb-1 block">Storage Type</label>
                    <select
                      value={nodeStorageType[n.node_id] || 'local'}
                      onChange={e => setNodeStorageType(prev => ({ ...prev, [n.node_id]: e.target.value }))}
                      className="w-full text-sm border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-400"
                    >
                      <option value="local">Local Filesystem</option>
                      <option value="s3">Amazon S3</option>
                      <option value="azure">Azure Blob Storage</option>
                      <option value="gcs">Google Cloud Storage</option>
                      <option value="sftp">SFTP Server</option>
                    </select>
                  </div>
                  {/* Folder / Path */}
                  <div>
                    <label className="text-[10px] font-medium text-slate-400 uppercase tracking-wide mb-1 block">Folder Path</label>
                    <input
                      type="text"
                      placeholder={
                        nodeStorageType[n.node_id] === 's3'    ? 's3://my-bucket/input-folder/' :
                        nodeStorageType[n.node_id] === 'azure' ? 'container/input-folder/' :
                        nodeStorageType[n.node_id] === 'sftp'  ? '/home/user/uploads/' :
                        'C:\\path\\to\\folder or /path/to/folder'
                      }
                      value={nodeFolderPath[n.node_id] || ''}
                      onChange={e => setNodeFolderPath(prev => ({ ...prev, [n.node_id]: e.target.value }))}
                      className={`w-full text-sm border font-mono bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-400 transition-colors ${
                        nodeFolderPath[n.node_id] ? 'border-emerald-300 dark:border-emerald-700' : 'border-slate-300 dark:border-slate-600'
                      }`}
                    />
                    {!nodeFolderPath[n.node_id] && (
                      <p className="text-[10px] text-amber-500 flex items-center gap-1 mt-1">
                        <Info className="w-3 h-3" /> Enter the folder path where files are stored for this tenant.
                      </p>
                    )}
                  </div>
                  {/* File Patterns */}
                  <div>
                    <label className="text-[10px] font-medium text-slate-400 uppercase tracking-wide mb-1 block">
                      File Patterns <span className="normal-case font-normal opacity-60">(optional, comma-separated)</span>
                    </label>
                    <input
                      type="text"
                      placeholder="*.pdf, *.docx, *.csv"
                      value={nodeFilePatterns[n.node_id] || ''}
                      onChange={e => setNodeFilePatterns(prev => ({ ...prev, [n.node_id]: e.target.value }))}
                      className="w-full text-sm border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-400 font-mono"
                    />
                  </div>
                </div>
              )}

              {/* ── DB SOURCE config — Connection + Table / SQL ── */}
              {n.role === 'source' && (
                <>
                  <div>
                    <label className="text-[10px] font-medium text-slate-400 uppercase tracking-wide mb-1 block">Connection</label>
                    <select
                      value={nodeConns[n.node_id] || ''}
                      onChange={e => setNodeConns(prev => ({ ...prev, [n.node_id]: e.target.value }))}
                      className={`w-full text-sm border bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-500 transition-colors ${
                        nodeConns[n.node_id] ? 'border-emerald-300 dark:border-emerald-700' : 'border-slate-300 dark:border-slate-600'
                      }`}
                    >
                      <option value="">— Select a connection —</option>
                      {connections.map(c => (
                        <option key={c.id} value={c.id}>{c.name || c.label || c.id}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-2">
                    {!showSql[n.node_id] ? (
                      <div>
                        <label className="text-[10px] font-medium text-slate-400 uppercase tracking-wide mb-1 block">Table Name</label>
                        <div className="flex gap-1.5">
                          <input
                            type="text"
                            placeholder="e.g. orders or dbo.orders"
                            value={nodeTable[n.node_id] || ''}
                            onChange={e => setNodeTable(prev => ({ ...prev, [n.node_id]: e.target.value }))}
                            className="flex-1 text-sm border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-500 font-mono"
                          />
                          <button
                            type="button"
                            onClick={() => { setShowSql(prev => ({ ...prev, [n.node_id]: true })); setNodeTable(prev => ({ ...prev, [n.node_id]: '' })); }}
                            className="text-[10px] px-2 py-1 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-400 hover:text-violet-600 hover:border-violet-300 transition-colors shrink-0"
                          >SQL ↓</button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <label className="text-[10px] font-medium text-slate-400 uppercase tracking-wide mb-1 block">Custom SQL Query</label>
                        <div className="flex gap-1.5 items-start">
                          <textarea
                            rows={3}
                            placeholder="SELECT * FROM orders WHERE status = 'active'"
                            value={nodeCustomSql[n.node_id] || ''}
                            onChange={e => setNodeCustomSql(prev => ({ ...prev, [n.node_id]: e.target.value }))}
                            className="flex-1 text-xs border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-500 font-mono resize-none"
                          />
                          <button
                            type="button"
                            onClick={() => { setShowSql(prev => ({ ...prev, [n.node_id]: false })); setNodeCustomSql(prev => ({ ...prev, [n.node_id]: '' })); }}
                            className="text-[10px] px-2 py-1 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-400 hover:text-violet-600 hover:border-violet-300 transition-colors shrink-0 mt-0.5"
                          >Table ↑</button>
                        </div>
                      </div>
                    )}
                    {!nodeTable[n.node_id] && !nodeCustomSql[n.node_id] && (
                      <p className="text-[10px] text-amber-500 flex items-center gap-1">
                        <Info className="w-3 h-3" /> Enter a table name or custom SQL query to run this node.
                      </p>
                    )}
                  </div>
                </>
              )}

              {/* ── SINK config — Connection + Destination Table ── */}
              {n.role === 'sink' && (
                <>
                  <div>
                    <label className="text-[10px] font-medium text-slate-400 uppercase tracking-wide mb-1 block">Connection</label>
                    <select
                      value={nodeConns[n.node_id] || ''}
                      onChange={e => setNodeConns(prev => ({ ...prev, [n.node_id]: e.target.value }))}
                      className={`w-full text-sm border bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-500 transition-colors ${
                        nodeConns[n.node_id] ? 'border-emerald-300 dark:border-emerald-700' : 'border-slate-300 dark:border-slate-600'
                      }`}
                    >
                      <option value="">— Select a connection —</option>
                      {connections.map(c => (
                        <option key={c.id} value={c.id}>{c.name || c.label || c.id}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] font-medium text-slate-400 uppercase tracking-wide mb-1 block">
                      Destination Table <span className="normal-case font-normal opacity-60">(optional)</span>
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. results_output"
                      value={nodeDestTable[n.node_id] || ''}
                      onChange={e => setNodeDestTable(prev => ({ ...prev, [n.node_id]: e.target.value }))}
                      className="w-full text-sm border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-500 font-mono"
                    />
                  </div>
                </>
              )}

            </div>
          ))}

          <div className="pt-2 border-t border-slate-100 dark:border-slate-800 flex justify-end">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 disabled:opacity-50 transition-all shadow-sm"
            >
              {saved ? <><CheckCircle2 className="w-4 h-4" /> Saved!</> : saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <><Save className="w-4 h-4" /> Save Config</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


// ─── Main panel ─────────────────────────────────────────────────────────────
export default function ConnectDataPanel({ bundleId, appName, onClose, initialTab, focusFlowId }) {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState(initialTab || 'dashboards');
  const [models, setModels] = useState([]);
  const [flows, setFlows] = useState([]);
  const [instanceId, setInstanceId] = useState(null);
  const [savedFlowConfig, setSavedFlowConfig] = useState({});
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [modelsRes, connsRes, flowsRes] = await Promise.all([
        apiFetch(`/tenant-portal/data-connect/bundles/${bundleId}/models`, token),
        apiFetch('/datasources', token),
        apiFetch(`/tenant-portal/data-connect/bundles/${bundleId}/flow-nodes`, token).catch(() => null),
      ]);
      setModels(Array.isArray(modelsRes) ? modelsRes : []);
      setConnections(Array.isArray(connsRes) ? connsRes : []);
      if (flowsRes) {
        setFlows(flowsRes.flows || []);
        setInstanceId(flowsRes.instance_id || null);
        setSavedFlowConfig({});
      }
      setError(null);
    } catch (err) {
      setError('Failed to load data settings.');
      console.error('[DataConnect] loadData error:', err);
    } finally {
      setLoading(false);
    }
  }, [bundleId, token]);

  useEffect(() => {
    loadData();
  }, [loadData, refreshKey]);

  const totalReady = models.filter(m => m.override?.data_status === 'READY').length;
  const totalModels = models.length;

  // Flow-level stats: count flows where ALL nodes have an override connection
  const flowsConfigured = flows.filter(f =>
    f.nodes.length > 0 && f.nodes.every(n => !!n.override_connection_id)
  ).length;
  const totalFlows = flows.length;
  const totalFlowNodes = flows.reduce((sum, f) => sum + f.nodes.length, 0);
  const totalFlowNodesMapped = flows.reduce(
    (sum, f) => sum + f.nodes.filter(n => !!n.override_connection_id).length, 0
  );

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[100]"
        onClick={onClose}
      />

      {/* Slide-over panel */}
      <div className="fixed right-0 top-0 h-full w-full max-w-xl bg-white dark:bg-slate-900 shadow-2xl z-[101] flex flex-col animate-slide-in-right">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
              <Database className="w-4 h-4 text-violet-600 dark:text-violet-400" />
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-900 dark:text-slate-100">
                Data Settings
              </h2>
              <p className="text-xs text-slate-500 dark:text-slate-400">{appName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tab toggle */}
        <div className="flex border-b border-slate-200 dark:border-slate-700 px-6">
          {[
            { id: 'dashboards', label: '📊 Dashboards', count: models.length },
            { id: 'flows',      label: '📋 Flows',      count: flows.length  },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2.5 px-3 text-sm font-medium border-b-2 transition-colors mr-2 ${
                activeTab === tab.id
                  ? 'border-violet-600 text-violet-600 dark:text-violet-400 dark:border-violet-400'
                  : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
              }`}
            >
              {tab.label}{tab.count > 0 && <span className="ml-1.5 text-xs bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded-full">{tab.count}</span>}
            </button>
          ))}
        </div>

        {/* Dashboard progress bar */}
        {activeTab === 'dashboards' && !loading && totalModels > 0 && (
          <div className="px-6 py-3 bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs text-slate-500 dark:text-slate-400">
                {totalReady} of {totalModels} model{totalModels !== 1 ? 's' : ''} connected
              </span>
              <span className="text-xs font-semibold text-violet-600 dark:text-violet-400">
                {Math.round((totalReady / totalModels) * 100)}%
              </span>
            </div>
            <div className="w-full h-1.5 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-violet-500 to-emerald-500 rounded-full transition-all duration-500"
                style={{ width: `${(totalReady / totalModels) * 100}%` }} />
            </div>
          </div>
        )}

        {/* Flows progress bar */}
        {activeTab === 'flows' && !loading && totalFlows > 0 && (
          <div className="px-6 py-3 bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs text-slate-500 dark:text-slate-400">
                {flowsConfigured} of {totalFlows} flow{totalFlows !== 1 ? 's' : ''} fully configured
                <span className="text-slate-400 dark:text-slate-500 ml-1">({totalFlowNodesMapped}/{totalFlowNodes} nodes)</span>
              </span>
              <span className={`text-xs font-semibold ${flowsConfigured === totalFlows ? 'text-emerald-600 dark:text-emerald-400' : 'text-violet-600 dark:text-violet-400'}`}>
                {totalFlows > 0 ? Math.round((flowsConfigured / totalFlows) * 100) : 0}%
              </span>
            </div>
            <div className="w-full h-1.5 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
              <div className={`h-full rounded-full transition-all duration-500 ${flowsConfigured === totalFlows ? 'bg-gradient-to-r from-emerald-500 to-emerald-400' : 'bg-gradient-to-r from-violet-500 to-emerald-500'}`}
                style={{ width: `${totalFlows > 0 ? (flowsConfigured / totalFlows) * 100 : 0}%` }} />
            </div>
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
          {loading && (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-violet-500" />
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm">
              <AlertCircle className="w-4 h-4 shrink-0" />
              {error}
            </div>
          )}

          {activeTab === 'dashboards' && !loading && !error && models.length === 0 && (
            <div className="text-center py-16">
              <Plug className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">
                No data models found for this app.
              </p>
              <p className="text-xs text-slate-400 mt-1">
                Dashboards in this bundle may not be using shared data models.
              </p>
            </div>
          )}

          {/* Dashboards tab */}
          {activeTab === 'dashboards' && !loading && models.map(model => (
            <ModelCard
              key={model.model_id}
              model={model}
              connections={connections}
              onRefreshStatus={() => setRefreshKey(k => k + 1)}
            />
          ))}

          {activeTab === 'flows' && !loading && flows.length === 0 && (
            <div className="text-center py-16">
              <Workflow className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">No flows with configurable nodes found in this bundle.</p>
            </div>
          )}
          {activeTab === 'flows' && !loading && (focusFlowId ? flows.filter(f => f.flow_id === focusFlowId) : flows).map(flow => (
            <FlowNodeCard
              key={flow.flow_id}
              flow={flow}
              connections={connections}
              instanceId={instanceId}
              savedOverrides={savedFlowConfig}
              onSaved={() => setRefreshKey(k => k + 1)}
              autoExpand={!!focusFlowId}
            />
          ))}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
          <p className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
            <Info className="w-3.5 h-3.5 shrink-0" />
            {activeTab === 'dashboards'
              ? 'Dashboards automatically update once sync is complete — no reload needed.'
              : 'Flow connections take effect on the next run — no restart needed.'}
          </p>
        </div>
      </div>

      {/* Slide-in animation */}
      <style>{`
        @keyframes slide-in-right {
          from { transform: translateX(100%); }
          to   { transform: translateX(0); }
        }
        .animate-slide-in-right {
          animation: slide-in-right 0.25s cubic-bezier(0.4, 0, 0.2, 1);
        }
      `}</style>
    </>
  );
}
